import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  getProducts,
  deleteProduct,
  type Product,
  type ProductFilters,
} from '@/api/productApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  discontinued: 'red',
};

const TYPE_COLORS: Record<string, string> = {
  product: 'blue',
  service: 'purple',
};

export default function ProductListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<ProductFilters>({});

  const canWrite = hasPermission('products', 'write');
  const canDelete = hasPermission('products', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof ProductFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => getProducts({ ...params, ...filters }),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('products.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteProduct(id);
            message.success(t('products.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Product> = useMemo(
    () => [
      {
        title: t('products.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        ellipsis: true,
        render: (name: string, record: Product) => (
          <a onClick={() => navigate(`/products/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('products.field.sku'),
        dataIndex: 'sku',
        key: 'sku',
        width: 120,
      },
      {
        title: t('products.field.type'),
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (type: string) => (
          <Tag color={TYPE_COLORS[type] ?? 'default'}>
            {t(`products.type.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('products.field.category'),
        dataIndex: 'category',
        key: 'category',
        width: 120,
        ellipsis: true,
      },
      {
        title: t('products.field.unit_price'),
        dataIndex: 'unit_price',
        key: 'unit_price',
        width: 110,
        sorter: true,
        align: 'right',
        render: (val: number, record: Product) =>
          `${record.currency} ${val.toLocaleString()}`,
      },
      {
        title: t('products.field.stock_quantity'),
        dataIndex: 'stock_quantity',
        key: 'stock_quantity',
        width: 100,
        sorter: true,
        align: 'right',
        render: (val: number, record: Product) => {
          const needsReorder = val <= record.reorder_level && record.type === 'product';
          return (
            <Space size="small">
              <span>{val}</span>
              {needsReorder && <WarningOutlined style={{ color: '#faad14' }} />}
            </Space>
          );
        },
      },
      {
        title: t('products.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`products.status.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 150,
        render: (_: unknown, record: Product) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/products/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/products/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const typeOptions = useMemo(
    () => [
      { value: '', label: t('products.filter_all') },
      { value: 'product', label: t('products.type.product') },
      { value: 'service', label: t('products.type.service') },
    ],
    [t]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('products.filter_all') },
      { value: 'active', label: t('products.status.active') },
      { value: 'inactive', label: t('products.status.inactive') },
      { value: 'discontinued', label: t('products.status.discontinued') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('products.field.type')}
        style={{ width: 120 }}
        allowClear
        options={typeOptions}
        onChange={(val) => handleFilterChange('type', val || undefined)}
      />
      <Select
        placeholder={t('products.field.status')}
        style={{ width: 140 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/products/new')}
        >
          {t('products.new_product')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('products.title')}</h2>
      <DataGrid<Product>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('products.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/products/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
